/**
 * src/features/admin/ui/CohortTable.tsx
 *
 * Trial → Paid cohort table (Phase 23 / METRIC-04).
 * Horizontally scrollable Card > overflow-x-auto > table with 7 columns.
 * Rate colored by threshold; conv columns in blue accent; started=0 rows → "—".
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/shared/ui/card';
import type { AdminMetrics } from '@/shared/types/admin';

// ---------------------------------------------------------------------------
// File-local helper (colocated to avoid shared-util for a single-use function)
// ---------------------------------------------------------------------------

function rateColor(rate: number): string {
  if (rate >= 0.35) return 'text-green-600 dark:text-green-400';
  if (rate < 0.25) return 'text-amber-600 dark:text-amber-400';
  return 'text-slate-900 dark:text-slate-50';
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CohortTableProps {
  cohorts: NonNullable<AdminMetrics['activation']>['cohorts'];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CohortTable({ cohorts }: CohortTableProps): React.ReactElement {
  const { t } = useTranslation();

  const thClass =
    'text-caption uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400 px-4 py-3 whitespace-nowrap';
  const tdBase = 'text-body font-mono tabular-nums px-4 py-3 whitespace-nowrap';
  const tdMuted = 'text-slate-400 dark:text-slate-500 px-4 py-3 whitespace-nowrap';

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-900/50">
                <th scope="col" className={thClass}>
                  {t('admin.activation.col_week', 'Week')}
                </th>
                <th scope="col" className={thClass}>
                  {t('admin.activation.col_started', 'Started')}
                </th>
                <th scope="col" className={thClass}>
                  {t('admin.activation.col_activated', 'Activated')}
                </th>
                <th scope="col" className={thClass}>
                  {t('admin.activation.col_rate', 'Act. Rate')}
                </th>
                <th scope="col" className={thClass}>
                  {t('admin.activation.col_conv_7d', 'Conv. 7d')}
                </th>
                <th scope="col" className={thClass}>
                  {t('admin.activation.col_conv_14d', 'Conv. 14d')}
                </th>
                <th scope="col" className={thClass}>
                  {t('admin.activation.col_conv_30d', 'Conv. 30d')}
                </th>
              </tr>
            </thead>
            <tbody>
              {cohorts.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="py-10 text-center text-body text-slate-400 dark:text-slate-500"
                  >
                    {t(
                      'admin.activation.no_data',
                      'No cohort data yet — waiting for first exports.',
                    )}
                  </td>
                </tr>
              ) : (
                cohorts.map((cohort) => {
                  const isEmpty = cohort.started === 0;
                  const ratePercent = isEmpty
                    ? '—'
                    : `${(cohort.activationRate * 100).toFixed(1)}%`;

                  return (
                    <tr
                      key={cohort.week}
                      className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-800/30"
                    >
                      <td
                        className={`font-semibold text-slate-700 dark:text-slate-300 px-4 py-3 whitespace-nowrap font-mono tabular-nums`}
                      >
                        {cohort.week}
                      </td>
                      {isEmpty ? (
                        <>
                          <td className={tdMuted}>—</td>
                          <td className={tdMuted}>—</td>
                          <td className={tdMuted}>—</td>
                          <td className={tdMuted}>—</td>
                          <td className={tdMuted}>—</td>
                          <td className={tdMuted}>—</td>
                        </>
                      ) : (
                        <>
                          <td className={`${tdBase} text-slate-900 dark:text-slate-50`}>
                            {cohort.started}
                          </td>
                          <td className={`${tdBase} text-slate-900 dark:text-slate-50`}>
                            {cohort.activated}
                          </td>
                          <td
                            className={`${tdBase} font-semibold ${rateColor(cohort.activationRate)}`}
                          >
                            {ratePercent}
                          </td>
                          <td
                            className={`${tdBase} text-blue-600 dark:text-blue-400`}
                          >
                            {cohort.converted7d}
                          </td>
                          <td
                            className={`${tdBase} text-blue-600 dark:text-blue-400`}
                          >
                            {cohort.converted14d}
                          </td>
                          <td
                            className={`${tdBase} text-blue-600 dark:text-blue-400`}
                          >
                            {cohort.converted30d}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
